/**
 * User management API functions.
 */
import apiClient from './client';

export interface UserCreateRequest {
  email: string;
  name: string;
  password: string;
}

export interface UserUpdateRequest {
  email?: string;
  name?: string;
  is_active?: boolean;
  is_admin?: boolean;
}

export interface UserListResponse {
  id: number;
  email: string;
  name: string;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
}

export interface UserDetailResponse {
  id: number;
  email: string;
  name: string;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  updated_at: string | null;
}

/**
 * User self-registration (sign-up).
 */
export const signup = async (userData: UserCreateRequest): Promise<UserDetailResponse> => {
  // Include CSRF token in request
  const { getCsrfHeader } = await import('./csrf');
  const csrfHeaders = await getCsrfHeader();
  
  const response = await apiClient.post<UserDetailResponse>(
    '/api/users/signup',
    userData,
    {
      headers: csrfHeaders,
    }
  );
  return response.data;
};

/**
 * Search for users by name or email (for sharing purposes).
 * Available to all authenticated users.
 */
export const searchUsers = async (query: string, limit: number = 50): Promise<UserListResponse[]> => {
  const response = await apiClient.get<UserListResponse[]>('/api/users/search', {
    params: { q: query, limit },
  });
  return response.data;
};

/**
 * List all users (admin only).
 */
export const listUsers = async (): Promise<UserListResponse[]> => {
  const response = await apiClient.get<UserListResponse[]>('/api/users');
  return response.data;
};

/**
 * Get user details.
 */
export const getUser = async (userId: number): Promise<UserDetailResponse> => {
  const response = await apiClient.get<UserDetailResponse>(`/api/users/${userId}`);
  return response.data;
};

/**
 * Update user (admin only).
 * SEC-007: Includes CSRF token for state-changing operation.
 */
export const updateUser = async (
  userId: number,
  userData: UserUpdateRequest
): Promise<UserDetailResponse> => {
  // Include CSRF token in request
  const { getCsrfHeader } = await import('./csrf');
  const csrfHeaders = await getCsrfHeader();
  
  const response = await apiClient.put<UserDetailResponse>(
    `/api/users/${userId}`,
    userData,
    {
      headers: csrfHeaders,
    }
  );
  return response.data;
};

/**
 * Delete user (admin only).
 * SEC-007: Includes CSRF token for state-changing operation.
 */
export const deleteUser = async (userId: number): Promise<void> => {
  // Include CSRF token in request
  const { getCsrfHeader } = await import('./csrf');
  const csrfHeaders = await getCsrfHeader();
  
  await apiClient.delete(`/api/users/${userId}`, {
    headers: csrfHeaders,
  });
};

/**
 * Update current user's profile.
 */
export interface UserProfileUpdate {
  email?: string;
  name?: string;
  password?: string;
}

/**
 * Update current user's profile.
 * SEC-007: Includes CSRF token for state-changing operation.
 */
export const updateCurrentUserProfile = async (
  profileData: UserProfileUpdate
): Promise<UserDetailResponse> => {
  // Include CSRF token in request
  const { getCsrfHeader } = await import('./csrf');
  const csrfHeaders = await getCsrfHeader();
  
  const response = await apiClient.put<UserDetailResponse>(
    '/api/users/me',
    profileData,
    {
      headers: csrfHeaders,
    }
  );
  return response.data;
};

